<template lang="pug">
.table.table--highlight
  .table__body
    table
      //-
      //- Table header
      //-
      thead
        tr
          th
          th {{ $t('simulator.traditionalStockExchange') }}
          th
            .gradient {{ $t('simulator.portfolioStockExchange') }}

      //-
      //- Table
      //-
      tbody
        tr(
          v-for="(fee, key) in fees"
          :key="key")
            //-
            //- Category fee
            //-
            td
              | {{ $t(`simulator.${String(key)}`) }}

              //- Custom text for csd category
              span.small.color-gray4.margin-left-quarter(v-if="key === 'csd'")
                | {{ $t('simulator.euroclear') }}

              //- Custom text for agentBank category
              span.small.color-gray4.margin-left-quarter(v-if="key === 'agentBank'")
                | {{ $t('simulator.participantEuroclear') }}

              //- Custom text for auditFirmSemi category
              span.small.color-gray4.margin-left-quarter(v-if="key === 'auditFirmSemi'")
                | {{ $t('simulator.semiannual') }}

              //- Custom text for extraCoordination category
              span.small.color-gray4.margin-left-quarter(v-if="key === 'extraCoordination'")
                span.margin-left-quarter ({{ coordinationHours }}
                  span.margin-right-quarter H
                  span / {{ coordinationRate }}
                  span.margin-left-quarter
                    sup €
                    | /
                    sub H
                  | )

            //-
            //- Traditional market fee
            //-
            td
              SimulatorCell(
                :value="getFeeValue(fee, 'traditional')"
                :locale="locale"
                :category="key"
                currency="EUR")

            //-
            //- Portfolio market fee
            //-
            td
              SimulatorCell(
                :value="getFeeValue(fee, 'portfolio')"
                :locale="locale"
                :category="key"
                currency="EUR")

        //-
        //- Total
        //-
        tr.row--total
          td {{ $t('simulator.total') }}
          td
            Amount(
              v-if="total.traditional"
              :value="total.traditional"
              :locale="locale"
              currency="EUR"
              is-integer)
          td
            .gradient
              Amount(
                v-if="total.portfolio"
                :value="total.portfolio"
                :locale="locale"
                currency="EUR"
                is-integer)

</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import Amount from '~/components/Amount.vue'
  import type {
    FeeEntry,
    FeeValue,
    IssuanceType,
    MarketTypeFees,
    MarketTypeTotalFees,
  } from '../types'
  import {
    EXTRA_COORDINATION_PORTFOLIO_HOURS,
    EXTRA_COORDINATION_RATE,
    EXTRA_COORDINATION_TRADITIONAL_HOURS,
  } from '../engine/feeEngine'
  import SimulatorCell from './SimulatorCell.vue'

  export interface SimulatorTableProps {
    /** Fee rows grouped by market type. */
    fees: MarketTypeFees
    /** Fee totals grouped by market type. */
    total: MarketTypeTotalFees
    /** Active locale used for amount formatting. */
    locale: string
    /** Selected issuance type. */
    issuanceType: IssuanceType
  }

  //
  // Props and events
  //
  const props = defineProps<SimulatorTableProps>()

  //
  // Data
  //
  const coordinationRate = EXTRA_COORDINATION_RATE

  //
  // Computed
  //
  const coordinationHours = computed(() =>
    props.issuanceType === 'migration'
      ? EXTRA_COORDINATION_PORTFOLIO_HOURS
      : EXTRA_COORDINATION_TRADITIONAL_HOURS,
  )

  //
  // Methods
  //
  function getFeeValue(fee: FeeEntry | null | undefined, type: keyof FeeEntry): FeeValue {
    return fee?.[type] ?? null
  }
</script>
